import { Tooltip, Typography, Stack } from '@mui/material';
import humanizeList from 'humanize-list';

import { tss } from 'tss-react/mui';

import { use } from 'react';

import { Or2Button } from '@/components/button/Or2Button';
import { getUserName } from '@/modules/user/utils/userName';

import { graphql, type DocumentType } from '@/gql';
import { useMutation } from '@/hooks/mutationWithSnackbar';
import { convertMarkdownToEmoji } from '@/utils/strings/emoji';
import { CurrentUserContext } from '@/context/CurrentUserContext';

export const ADD_REACTION_MUTATION = graphql(`
  mutation AddReaction($data: AddReactionToPostInput) {
    addReactionToPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

export const REMOVE_REACTION_MUTATION = graphql(`
  mutation RemoveReaction($data: RemoveReactionFromPostInput) {
    removeReactionFromPost(params: $data) {
      post {
        ...postFields
      }
    }
  }
`);

const POST_REACTION_POST_FRAGMENT = graphql(`
  fragment postReactionPostFields on Post {
    _id
  }
`);

export interface ReactionGroupUser {
  _id: string | null | undefined;
  firstName: string | null | undefined;
  lastName: string | null | undefined;
  reactionId: string | null | undefined;
}

export interface ReactionGroup {
  reaction: string;
  skinTones: string[];
  firstCreated: Date;
  users: ReactionGroupUser[];
}

interface Props {
  reactionGroup: ReactionGroup;
  post: DocumentType<typeof POST_REACTION_POST_FRAGMENT>;
}

const useStyles = tss.create(({ theme }) => {
  return {
    tooltip: {
      textAlign: 'center',
      maxWidth: 200,
      fontSize: theme.typography.caption.fontSize,
    },
  };
});

const getReadableReaction = (reaction: string) => {
  return reaction.replace(/:/g, '').replace(/[-_]/g, ' ');
};

const getHumanizedUserNames = (
  users: ReactionGroupUser[],
  currentUserId: string | null,
) => {
  const hasCurrentUser = users.some((user) => user._id === currentUserId);
  const userNames = users
    .filter((user) => user._id !== currentUserId)
    .map((user) => getUserName(user));

  if (hasCurrentUser) {
    userNames.push(userNames.length > 0 ? 'you' : 'You (click to remove)');
  }

  return humanizeList(userNames);
};

export const PostReaction = ({ reactionGroup, post }: Props) => {
  const { currentUserId } = use(CurrentUserContext);
  const { classes } = useStyles();

  const [addReactionToPost, { loading: loadingAdd }] = useMutation(
    ADD_REACTION_MUTATION,
    {
      completionMessage: 'Reaction added',
    },
  );

  const [removeReactionFromPost, { loading: loadingRemove }] = useMutation(
    REMOVE_REACTION_MUTATION,
    {
      completionMessage: 'Reaction removed',
    },
  );

  const { reaction, skinTones, users } = reactionGroup;
  const { _id: postId } = post;

  const reactionsWithTones = [
    ...new Set([...skinTones.map((skinTone) => reaction + skinTone)]),
  ];

  const reactionsToDisplay =
    reactionsWithTones.length > 0 ? reactionsWithTones : [reaction];

  const humanizedUserNames = getHumanizedUserNames(users, currentUserId || '');
  const humanizedReaction = getReadableReaction(reaction);

  return (
    <Tooltip
      title={
        <div className={classes.tooltip}>
          <Typography>
            <strong>{humanizedUserNames}</strong> reacted with &quot;
            {humanizedReaction}
            &quot;
          </Typography>
        </div>
      }
      placement="top"
    >
      <span>
        <Or2Button
          disabled={loadingAdd || loadingRemove}
          size="small"
          color="secondary"
          onClick={() => {
            const userReaction = users.find(
              (user) => user._id === currentUserId,
            );

            if (userReaction?.reactionId) {
              removeReactionFromPost({
                variables: {
                  data: {
                    reaction: userReaction.reactionId,
                    post: postId,
                  },
                },
              });
            } else {
              addReactionToPost({
                variables: {
                  data: {
                    reaction,
                    post: postId,
                  },
                },
              });
            }
          }}
        >
          <Stack direction="row" alignItems="center" spacing={-1}>
            {reactionsToDisplay.map((reaction) => (
              <Typography key={reaction} variant="h6" sx={{ paddingRight: 1 }}>
                {convertMarkdownToEmoji(reaction)}
              </Typography>
            ))}
          </Stack>
          &nbsp;
          <Typography variant="button">{users.length}</Typography>
        </Or2Button>
      </span>
    </Tooltip>
  );
};
